import { useEffect, type RefObject } from "react";
import {
  prefersReducedMotion,
  whenImageCanPaint,
} from "./playHeroWaveWipe";

/**
 * Homepage dual-event hero intro: soft dual-panel reveal, then wave.
 */
export function useDualEventHeroGsap(rootRef: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const events = Array.from(root.querySelectorAll<HTMLElement>(".dual-event-hero-event"));
    const wave = root.querySelector<HTMLElement>(".dual-event-hero-wave");
    const mark = root.querySelector<HTMLElement>(".dual-event-hero-x");

    if (prefersReducedMotion()) {
      events.forEach((el) => el.classList.add("dual-event-hero-event--shown"));
      wave?.classList.add("dual-event-hero-wave--shown");
      mark?.classList.add("dual-event-hero-x--shown");
      return;
    }

    let cancelled = false;
    let timeline: { kill: () => void } | null = null;

    const revealWithoutGsap = () => {
      events.forEach((el) => el.classList.add("dual-event-hero-event--shown"));
      wave?.classList.add("dual-event-hero-wave--shown");
      mark?.classList.add("dual-event-hero-x--shown");
    };

    void (async () => {
      const paintTargets = Array.from(
        root.querySelectorAll<HTMLImageElement>(
          ".dual-event-hero-event-image, .dual-event-hero-event-logo, .dual-event-hero-x-mark, .dual-event-hero-play-mark, .dual-event-hero-wave-img",
        ),
      );
      await Promise.all(paintTargets.map((img) => whenImageCanPaint(img)));
      if (cancelled) return;

      try {
        const { gsap } = await import("gsap");
        if (cancelled || !rootRef.current) return;

        // Soft settle instead of a lateral slide — full-bleed panels jump with large x offsets.
        gsap.set(events, {
          autoAlpha: 0,
          scale: 1.03,
          transformOrigin: "50% 50%",
          force3D: true,
        });

        if (wave) {
          gsap.set(wave, {
            autoAlpha: 1,
            clipPath: "inset(0 100% 0 0)",
          });
        }

        if (mark) {
          // Opacity only. autoAlpha also sets visibility:hidden, which blocks clicks until the wave ends.
          gsap.set(mark, { opacity: 0 });
        }

        const tl = gsap.timeline({
          defaults: { ease: "power2.out" },
          onComplete: () => {
            events.forEach((el) => {
              el.classList.add("dual-event-hero-event--shown");
              gsap.set(el, { clearProps: "transform" });
            });
            wave?.classList.add("dual-event-hero-wave--shown");
            if (wave) gsap.set(wave, { clearProps: "clipPath" });
            mark?.classList.add("dual-event-hero-x--shown");
            if (mark) gsap.set(mark, { clearProps: "opacity" });
          },
        });
        timeline = tl;

        if (events.length) {
          tl.to(
            events,
            {
              autoAlpha: 1,
              scale: 1,
              duration: 1.4,
              stagger: 0.16,
              overwrite: "auto",
            },
            0.08,
          );
        }

        if (mark) {
          tl.to(
            mark,
            {
              opacity: 1,
              duration: 1.4,
              overwrite: "auto",
            },
            0.08,
          );
        }

        if (wave) {
          tl.to(
            wave,
            {
              clipPath: "inset(0 0% 0 0)",
              duration: 1.75,
              ease: "power2.inOut",
              overwrite: "auto",
            },
            0.45,
          );
        }
      } catch {
        revealWithoutGsap();
      }
    })();

    return () => {
      cancelled = true;
      timeline?.kill();
    };
  }, [rootRef]);
}
